"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LOOKBACK_HOURS = void 0;
exports.buildCorrelations = buildCorrelations;
exports.summariseSuspects = summariseSuspects;
/**
 * Symptoms against what preceded them.
 *
 * This is the question the whole log exists to answer: when Richard sounded impaired,
 * what had he taken, and how long before? The paper chart put doses and symptoms in
 * adjacent columns and left the arithmetic to whoever was reading it.
 *
 * It correlates; it does not conclude. "Propranolol 83 minutes earlier" is an
 * observation a clinician can weigh. Anything that looked like a verdict would be this
 * software practising medicine, which it must not do.
 */
const domain_js_1 = require("./domain.js");
/** How far back to look. Six hours covers the plausible window for these medicines. */
exports.LOOKBACK_HOURS = 6;
function buildCorrelations(events, lookbackHours = exports.LOOKBACK_HOURS) {
    const live = events.filter((e) => e.voidedAt == null);
    const windowMs = lookbackHours * 60 * 60 * 1000;
    const intake = live
        .filter((e) => e.eventType === 'DOSE_TAKEN' || e.eventType === 'EXTRA_SUBSTANCE')
        .sort((a, b) => a.takenAtUtc.localeCompare(b.takenAtUtc));
    return live
        .filter((e) => e.eventType === 'SYMPTOM')
        .sort((a, b) => b.takenAtUtc.localeCompare(a.takenAtUtc))
        .map((symptom) => {
        const at = Date.parse(symptom.takenAtUtc);
        const preceding = intake
            .filter((i) => {
            const t = Date.parse(i.takenAtUtc);
            // Strictly before: something taken after the symptom cannot have caused it.
            return t <= at && at - t <= windowMs;
        })
            .map((i) => toPreceding(i, at))
            .sort((a, b) => a.minutesBefore - b.minutesBefore);
        const episode = {
            eventId: symptom.eventId,
            logicalDate: symptom.logicalDate,
            localTime: symptom.localTime ?? '',
            takenAtUtc: symptom.takenAtUtc,
            symptoms: symptom.symptoms ?? [],
            redFlag: symptom.redFlag === true,
            preceding,
            hadAmplifier: preceding.some((p) => p.amplifier === true),
            hadExtraDose: preceding.some((p) => p.extraDose === true),
        };
        if (symptom.severity !== undefined)
            episode.severity = symptom.severity;
        return episode;
    });
}
function toPreceding(event, symptomAt) {
    const minutesBefore = Math.round((symptomAt - Date.parse(event.takenAtUtc)) / 60_000);
    if (event.eventType === 'DOSE_TAKEN') {
        return {
            kind: 'DOSE',
            label: event.medId ?? 'dose',
            minutesBefore,
            ...(event.extraDose === true ? { extraDose: true } : {}),
        };
    }
    return {
        kind: 'EXTRA',
        label: event.substanceName ?? event.substanceKey ?? 'something',
        minutesBefore,
        ...(event.substanceKey && (0, domain_js_1.isAmplifier)(event.substanceKey) ? { amplifier: true } : {}),
    };
}
function summariseSuspects(episodes) {
    const byLabel = new Map();
    for (const episode of episodes) {
        // Once per episode: two doses of the same medicine in one window is still one
        // episode, and counting both would inflate the apparent association.
        const seen = new Set();
        for (const item of episode.preceding) {
            if (seen.has(item.label))
                continue;
            seen.add(item.label);
            const gaps = byLabel.get(item.label) ?? [];
            gaps.push(item.minutesBefore);
            byLabel.set(item.label, gaps);
        }
    }
    return [...byLabel.entries()]
        .map(([label, gaps]) => ({
        label,
        episodes: gaps.length,
        totalEpisodes: episodes.length,
        medianMinutesBefore: median(gaps),
    }))
        .sort((a, b) => b.episodes - a.episodes || a.medianMinutesBefore - b.medianMinutesBefore);
}
function median(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length === 0)
        return 0;
    return sorted.length % 2 === 0
        ? Math.round(((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2)
        : (sorted[mid] ?? 0);
}
//# sourceMappingURL=correlation.js.map